import { Reducer, useEffect, useReducer } from "react";

import { isVerseMedical } from "../utils/envConfig";
import useKeyPressOnElement from "./useKeyPressOnElement";

type State = {
  selectedIndex: number;
};

type ActionType = "arrowUp" | "arrowDown";

interface Action {
  type: ActionType;
  listLength: number;
}

const reducer: Reducer<State, Action> = (state, action) => {
  const listLength = action.listLength;
  switch (action.type) {
    case "arrowUp":
      return {
        selectedIndex:
          state.selectedIndex !== 0 ? state.selectedIndex - 1 : listLength - 1,
      };
    case "arrowDown":
      return {
        selectedIndex:
          state.selectedIndex !== listLength - 1 ? state.selectedIndex + 1 : 0,
      };
    default:
      return {
        selectedIndex: state.selectedIndex,
      };
  }
};

/**
 * This Hook can be used for keyboard navigation in a list of items.
 * @param {number} listLength - The length of the list of items.
 * @param {number} selectedIndex - The index of the selected item, defaults to 0.
 * @param {function} selectOption - The function to call when an option is selected.
 * @param {boolean} isDropdownOpen - A boolean to check if the dropdown is open.
 * @param {function} toggleDropdown - The function to call to toggle the dropdown.
 * @param {HTMLElement} dropDownListElement - The focused drop down element. Optional.
 * @returns {Array} - Returns an array containing the state and dispatch function.
 * @example
 * const [state, dispatch] = useKeyboardNavigation(listLength, selectedIndex, selectOption, isDropdownOpen, toggleDropdown);
 * useEffect(() => {
 *  if (state.selectedIndex !== undefined) {
 *   selectOption(state.selectedIndex);
 * }
 * }, [state.selectedIndex]);
 */
const useKeyboardNavigation = (
  listLength: number,
  selectedIndex: number = 0,
  selectOption: (selectedIndex: number) => void,
  isDropdownOpen: boolean,
  toggleDropdown: () => void,
  dropDownListElement?: HTMLElement | null,
): [State, React.Dispatch<Action>] => {
  const arrowUpPressed = useKeyPressOnElement(
    "ArrowUp",
    dropDownListElement || null,
  );
  const arrowDownPressed = useKeyPressOnElement(
    "ArrowDown",
    dropDownListElement || null,
  );
  const enterPressed = useKeyPressOnElement(
    "Enter",
    dropDownListElement || null,
  );
  const escPressed = useKeyPressOnElement(
    "Escape",
    dropDownListElement || null,
  );
  if (isVerseMedical()) {
    selectedIndex = -1;
  }
  const initialState = { selectedIndex: selectedIndex };
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (arrowUpPressed) {
      dispatch({ type: "arrowUp", listLength });
    }
  }, [arrowUpPressed, listLength]);

  useEffect(() => {
    if (arrowDownPressed) {
      dispatch({ type: "arrowDown", listLength });
    }
  }, [arrowDownPressed, listLength]);

  useEffect(() => {
    if (enterPressed) {
      if (!isDropdownOpen) {
        toggleDropdown();
      } else if (state.selectedIndex !== undefined) {
        selectOption(state.selectedIndex);
      }
    }
  }, [enterPressed]);

  useEffect(() => {
    if (escPressed && isDropdownOpen) {
      toggleDropdown();
    }
  }, [escPressed]);

  return [state, dispatch];
};

export default useKeyboardNavigation;
